import React, { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Plus,
  Save,
  Trash2,
  FileText,
  CheckCircle2,
  Circle,
  ExternalLink,
  X,
  Eye,
  RefreshCw,
  Sparkles,
  Loader2,
  Send,
  MailCheck,
  Mail,
  ImageIcon,
} from 'lucide-react';

// Lucide dropped brand glyphs in recent versions, so we ship a tiny inline
// Facebook "f" mark for the Import button. Keeps brand recognition without
// reintroducing a brand-icon dependency.
const FacebookGlyph = ({ className = 'h-4 w-4' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M13.5 22v-8h2.7l.4-3.2h-3.1V8.7c0-.9.3-1.6 1.7-1.6h1.6V4.2c-.3 0-1.2-.2-2.4-.2-2.4 0-4 1.5-4 4.1v2.7H7.6V14h2.8v8h3.1z" />
  </svg>
);
import { toast } from 'sonner';
import { logger } from '../lib/logger';

const EMPTY_FORM = {
  id: null,
  title: '',
  slug: '',
  subtitle: '',
  excerpt: '',
  html: '',
  tags: '',
  hero_image_url: '',
  meta_title: '',
  meta_description: '',
  canonical_url: '',
  status: 'draft',
  // Provenance carried through from Import flows (e.g. "facebook"). Never
  // shown to readers — just persisted on first save so we can audit later.
  import_source: null,
  import_metadata: null,
  // Phase 27 — when a post transitions to published in this save, the server
  // fans out a teaser email to the matching subscriber cohort. Default ON so
  // the common "Publish" click Just Works; admin can uncheck to publish
  // silently (e.g. correction posts).
  send_to_subscribers_on_publish: true,
};

const EMPTY_FB_IMPORT = {
  facebook_url: '',
  text: '',
  image_url: '',
  use_ai: true,
};

function slugify(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120);
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null); // null = closed, otherwise EMPTY_FORM-shaped
  const [autoSlug, setAutoSlug] = useState(true);
  // Phase 26 — "Import from Facebook" sub-modal. When non-null, this modal is
  // open. On successful conversion we close it and pre-populate the existing
  // editor with the returned draft fields — admin reviews/edits before saving.
  const [fbImport, setFbImport] = useState(null);
  const [fbImporting, setFbImporting] = useState(false);
  const [healingHeroes, setHealingHeroes] = useState(false);
  const [healingMedia, setHealingMedia] = useState(false);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/blog/posts');
      setPosts(data?.posts || []);
    } catch (err) {
      logger.error('AdminBlog load failed', err);
      toast.error('Could not load blog posts');
    } finally {
      setLoading(false);
    }
  };

  // Phase 36 — heal broken hero images.
  // Scans every post; any whose hero_image_url is missing or points at our
  // ephemeral /api/media/ path gets swapped for a durable CDN URL extracted
  // from the post body. Idempotent. Safe to run repeatedly.
  const handleHealHeroImages = async () => {
    const confirmed = window.confirm(
      'Scan all posts and replace broken hero images with a durable URL from the article body?\n\n' +
      'Only affects posts whose current hero points to /api/media/ (ephemeral) or is missing. ' +
      'Posts with good hero images are left untouched.'
    );
    if (!confirmed) return;
    setHealingHeroes(true);
    try {
      const res = await api.post('/api/admin/blog/heal-hero-images', {});
      const healed = res?.healed_count ?? 0;
      const scanned = res?.scanned ?? 0;
      const unfixable = (res?.no_body_image_found || []).length;
      if (healed > 0) {
        toast.success(`Healed ${healed} of ${scanned} posts.`, {
          description: unfixable
            ? `${unfixable} still need a manual hero (no image in body).`
            : 'All broken heroes fixed.',
        });
      } else if (unfixable > 0) {
        toast.warning(`No posts healed automatically`, {
          description: `${unfixable} posts have a broken hero but no image in the body to substitute.`,
        });
      } else {
        toast.info('All hero images already look good.', {
          description: `Scanned ${scanned} posts. Nothing to fix.`,
        });
      }
      await loadPosts();
    } catch (err) {
      logger.error('AdminBlog heal failed', err);
      toast.error('Heal failed', { description: String(err?.message || err) });
    } finally {
      setHealingHeroes(false);
    }
  };

  // Phase 36 — one-click full media backfill.
  // Runs both passes of scripts/heal_media.py: (1) refetch bytes for
  // blog_media docs missing their bytes payload, and (2) walk every blog
  // post to fetch external <img> URLs into MongoDB and rewrite src attributes
  // to /api/media/ URLs. Idempotent — safe to click repeatedly.
  const handleHealMedia = async () => {
    const confirmed = window.confirm(
      'Run the full media backfill?\n\n' +
      'This will:\n' +
      '  1. Refetch bytes for any broken media records (fixing 404s)\n' +
      '  2. Download every external image in every post and rehost it on our servers\n' +
      '  3. Rewrite <img> src attributes in post bodies to use our own URLs\n\n' +
      'This is idempotent — safe to run repeatedly. May take up to 30 seconds ' +
      'depending on how many images need to be fetched.'
    );
    if (!confirmed) return;
    setHealingMedia(true);
    try {
      const res = await api.post('/api/admin/blog/heal-media', {});
      const media = res?.media || {};
      const posts = res?.posts || {};
      const mediaHealed = (media.healed || []).length;
      const mediaFailed = (media.failed || []).length;
      const postsChanged = (posts.changed || []).length;
      const summary = [
        `Media: ${mediaHealed} refetched`,
        mediaFailed ? `${mediaFailed} failed` : null,
        `Posts: ${postsChanged} rewritten`,
      ].filter(Boolean).join(' · ');
      if (mediaHealed > 0 || postsChanged > 0) {
        toast.success('Media backfill complete', { description: summary });
      } else {
        toast.info('Nothing to fix', { description: summary });
      }
      await loadPosts();
    } catch (err) {
      logger.error('AdminBlog heal-media failed', err);
      toast.error('Backfill failed', { description: String(err?.message || err) });
    } finally {
      setHealingMedia(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const openNew = () => {
    setEditor({ ...EMPTY_FORM });
    setAutoSlug(true);
  };

  const openEdit = (post) => {
    setEditor({
      id: post.id,
      title: post.title || '',
      slug: post.slug || '',
      subtitle: post.subtitle || '',
      excerpt: post.excerpt || '',
      html: post.html || '',
      tags: (post.tags || []).join(', '),
      hero_image_url: post.hero_image_url || '',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      canonical_url: post.canonical_url || '',
      status: post.status || 'draft',
      import_source: post.import_source || null,
      import_metadata: post.import_metadata || null,
      // Editing an already-published post: default the toggle to OFF so
      // routine edits don't accidentally re-blast subscribers. Sending a
      // blast for an existing published post is done via the row's
      // "Send to subscribers" button (which has its own confirm).
      send_to_subscribers_on_publish: post.status === 'published' ? false : true,
    });
    setAutoSlug(false);
  };

  // ---- "Import from Facebook" handlers (Phase 26) -------------------------
  const openFbImport = () => setFbImport({ ...EMPTY_FB_IMPORT });
  const closeFbImport = () => { if (!fbImporting) setFbImport(null); };
  const updateFbField = (field, value) => {
    setFbImport((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleFbCreateDraft = async () => {
    if (!fbImport) return;
    const text = (fbImport.text || '').trim();
    if (!text) {
      toast.error('Paste the Facebook post text first');
      return;
    }
    setFbImporting(true);
    try {
      const data = await api.post('/api/admin/blog/import/facebook', {
        facebook_url: fbImport.facebook_url.trim() || null,
        text,
        image_url: fbImport.image_url.trim() || null,
        use_ai: !!fbImport.use_ai,
      });
      const draft = data?.draft;
      if (!draft) throw new Error('Empty draft from server');
      // Pre-populate the existing editor — admin reviews/edits before save.
      setEditor({
        ...EMPTY_FORM,
        title: draft.title || '',
        slug: draft.slug || '',
        subtitle: draft.subtitle || '',
        excerpt: draft.excerpt || '',
        html: draft.html || '',
        tags: (draft.tags || []).join(', '),
        hero_image_url: draft.hero_image_url || '',
        meta_title: draft.meta_title || '',
        meta_description: draft.meta_description || '',
        canonical_url: draft.canonical_url || '',
        status: 'draft',
        import_source: draft.import_source || 'facebook',
        import_metadata: draft.import_metadata || null,
      });
      setAutoSlug(false);
      setFbImport(null);
      toast.success(
        fbImport.use_ai
          ? 'Draft created with AI formatting — review and publish when ready'
          : 'Draft created — review and publish when ready',
      );
    } catch (err) {
      logger.error('AdminBlog FB import failed', err);
      toast.error(err.message || 'Could not create draft from Facebook content');
    } finally {
      setFbImporting(false);
    }
  };

  const closeEditor = () => {
    setEditor(null);
  };

  const updateField = (field, value) => {
    setEditor((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      if (field === 'title' && autoSlug) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const handleSave = async (publish = false) => {
    if (!editor) return;
    if (!editor.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!editor.html.trim()) {
      toast.error('HTML body is required');
      return;
    }
    setSaving(true);
    const payload = {
      title: editor.title.trim(),
      slug: editor.slug.trim() || undefined,
      subtitle: editor.subtitle.trim() || null,
      excerpt: editor.excerpt.trim() || null,
      html: editor.html,
      tags: editor.tags.split(',').map((t) => t.trim()).filter(Boolean),
      hero_image_url: editor.hero_image_url.trim() || null,
      meta_title: editor.meta_title.trim() || null,
      meta_description: editor.meta_description.trim() || null,
      canonical_url: editor.canonical_url.trim() || null,
      status: publish ? 'published' : editor.status || 'draft',
      // Phase 27 — only forwards the publish→blast intent. The backend
      // ignores this field unless the status actually transitions to
      // "published" in this same request (or on initial create with that
      // status), so it's safe to send on every save.
      send_to_subscribers_on_publish: !!editor.send_to_subscribers_on_publish,
    };
    // Pass provenance through only on initial create (not on later edits,
    // since the existing doc already has it).
    if (!editor.id && editor.import_source) {
      payload.import_source = editor.import_source;
      payload.import_metadata = editor.import_metadata || null;
    }
    try {
      let result;
      if (editor.id) {
        result = await api.patch(`/api/admin/blog/posts/${editor.id}`, payload);
      } else {
        result = await api.post('/api/admin/blog/posts', payload);
      }
      // Phase 27 — show the blast result when the publish flow fanned out.
      const blast = result?.post?.send_result;
      if (publish && blast?.ok && blast.sent > 0) {
        toast.success(
          `Post published — sent to ${blast.sent} subscriber${blast.sent === 1 ? '' : 's'}${blast.failed ? ` (${blast.failed} failed)` : ''}`,
        );
      } else if (publish && blast?.ok && blast.total === 0) {
        toast.success('Post published — no matching subscribers to email');
      } else if (publish && blast?.note === 'Resend not configured') {
        toast.success('Post published — email service not configured, blast skipped');
      } else {
        toast.success(
          publish ? 'Post published' : editor.id ? 'Post saved' : 'Draft created',
        );
      }
      closeEditor();
      // Optimistic merge so the new/updated row shows instantly.
      if (result?.post) {
        setPosts((prev) => {
          const without = prev.filter((p) => p.id !== result.post.id);
          return [result.post, ...without];
        });
      } else {
        loadPosts();
      }
    } catch (err) {
      logger.error('AdminBlog save failed', err);
      toast.error(err.message || 'Could not save post');
    } finally {
      setSaving(false);
    }
  };

  // Phase 27 — manual re-blast a published post to its subscriber cohort.
  // Always behind a confirm dialog because a blast is irreversible.
  const handleSendBlast = async (post) => {
    const msg =
      `Send "${post.title}" to subscribers now?\n\n` +
      `This emails matching subscribers (smart-routed by post tags). ` +
      `Cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const data = await api.post(`/api/admin/blog/posts/${post.id}/send`, {});
      const res = data?.result;
      if (res?.note === 'Resend not configured') {
        toast.error('Email service not configured — nothing was sent');
      } else if (res?.total === 0) {
        toast.success('Send complete — no matching subscribers');
      } else {
        toast.success(
          `Sent to ${res?.sent ?? 0} subscriber${res?.sent === 1 ? '' : 's'}${res?.failed ? ` (${res.failed} failed)` : ''}`,
        );
      }
      // Refresh just this row so the "last_blast" stamp shows up.
      loadPosts();
    } catch (err) {
      logger.error('AdminBlog send blast failed', err);
      toast.error(err.message || 'Could not send blast');
    }
  };

  const handleTogglePublish = async (post) => {
    const nextStatus = post.status === 'published' ? 'draft' : 'published';
    try {
      const result = await api.patch(`/api/admin/blog/posts/${post.id}`, { status: nextStatus });
      toast.success(nextStatus === 'published' ? 'Post published' : 'Moved to draft');
      if (result?.post) {
        setPosts((prev) => prev.map((p) => (p.id === result.post.id ? result.post : p)));
      } else {
        loadPosts();
      }
    } catch (err) {
      toast.error(err.message || 'Could not update status');
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.del(`/api/admin/blog/posts/${post.id}`);
      toast.success('Post deleted');
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err) {
      toast.error(err.message || 'Could not delete');
    }
  };

  const stats = useMemo(() => {
    const published = posts.filter((p) => p.status === 'published').length;
    return { total: posts.length, published, drafts: posts.length - published };
  }, [posts]);

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-foreground/60 font-semibold mb-1">
            The Apex Journal
          </div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Blog posts</h2>
          <p className="text-[13.5px] text-foreground/60 mt-1">
            Paste HTML from Marblism (or anywhere) and publish to apexathleticmind.com/blog.
            Posts auto-include JSON-LD, OG cards, canonical URLs, and feed into the sitemap + RSS.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadPosts} disabled={loading} data-testid="admin-blog-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleHealHeroImages}
            disabled={healingHeroes || loading}
            className="border-[#4FBCC7]/40 text-[#004C54] hover:bg-[#4FBCC7]/10"
            data-testid="admin-blog-heal-heroes"
            title="Scan all posts and replace broken /api/media/ hero URLs with a durable CDN URL from the article body."
          >
            {healingHeroes ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <ImageIcon className="h-4 w-4 mr-1.5" />
            )}
            Heal hero images
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleHealMedia}
            disabled={healingMedia || loading}
            className="border-[#004C54]/40 text-[#004C54] hover:bg-[#004C54]/10"
            data-testid="admin-blog-heal-media"
            title="Full backfill — refetch broken media AND rewrite every external <img> in every post to use our own /api/media/ URLs."
          >
            {healingMedia ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-1.5" />
            )}
            Rehost all media
          </Button>
          {/* Phase 26 — "Import from Facebook" button hidden by request.
              Backend endpoint (/api/admin/blog/import/facebook), modal,
              and handlers remain in place so this can be re-enabled by
              uncommenting the block below — no rewiring needed. */}
          {/*
          <Button
            variant="outline"
            size="sm"
            onClick={openFbImport}
            className="border-[#1877F2]/30 text-[#1877F2] hover:bg-[#1877F2]/10 hover:text-[#1877F2]"
            data-testid="admin-blog-import-facebook"
          >
            <FacebookGlyph className="h-4 w-4 mr-1.5" />
            Import from Facebook
          </Button>
          */}
          <Button onClick={openNew} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="admin-blog-new">
            <Plus className="h-4 w-4 mr-1.5" />
            New post
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card><CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold">Total</div>
          <div className="text-2xl font-heading font-bold mt-1" data-testid="admin-blog-stat-total">{stats.total}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold">Published</div>
          <div className="text-2xl font-heading font-bold mt-1 text-[#004C54]" data-testid="admin-blog-stat-published">{stats.published}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 font-mono-accent font-semibold">Drafts</div>
          <div className="text-2xl font-heading font-bold mt-1 text-foreground/70" data-testid="admin-blog-stat-drafts">{stats.drafts}</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && posts.length === 0 ? (
            <div className="p-8 text-center text-sm text-foreground/60" data-testid="admin-blog-loading">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="p-10 text-center" data-testid="admin-blog-empty">
              <FileText className="h-8 w-8 mx-auto text-foreground/30 mb-3" />
              <p className="text-sm text-foreground/60">No posts yet. Click <strong>New post</strong> to create your first essay.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {posts.map((p) => (
                <li key={p.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-blog-row-${p.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {p.status === 'published' ? (
                        <Badge className="bg-[#004C54]/10 text-[#004C54] hover:bg-[#004C54]/10 border-0"><CheckCircle2 className="h-3 w-3 mr-1" />Published</Badge>
                      ) : (
                        <Badge variant="outline" className="text-foreground/60"><Circle className="h-3 w-3 mr-1" />Draft</Badge>
                      )}
                      {p.import_source && (
                        <Badge
                          variant="outline"
                          className="border-[#4FBCC7]/40 text-[#004C54] bg-[#4FBCC7]/10 text-[10px] uppercase tracking-[0.14em] font-mono-accent font-bold px-2 py-0.5"
                          data-testid={`admin-blog-import-badge-${p.slug}`}
                          title={`Imported from ${p.import_source}${p.import_metadata?.author ? ` · by ${p.import_metadata.author}` : ''}`}
                        >
                          {p.import_source}
                        </Badge>
                      )}
                      {p.tags?.slice(0, 3).map((t) => (
                        <span key={t} className="font-mono-accent uppercase tracking-[0.18em] text-[9px] text-foreground/55 font-semibold">#{t}</span>
                      ))}
                    </div>
                    <div className="font-heading text-[15px] font-semibold truncate">{p.title}</div>
                    <div className="text-[12px] text-foreground/55 truncate mt-0.5">
                      /blog/{p.slug}
                      {p.published_at ? ` · ${new Date(p.published_at).toLocaleDateString()}` : ''}
                    </div>
                    {p.last_blast?.ok && p.last_blast?.total > 0 && (
                      <div
                        className="text-[11px] text-[#004C54] font-mono-accent uppercase tracking-[0.16em] mt-1 font-semibold inline-flex items-center gap-1"
                        data-testid={`admin-blog-last-blast-${p.slug}`}
                      >
                        <MailCheck className="h-3 w-3" />
                        Sent to {p.last_blast.sent}/{p.last_blast.total}
                        {p.last_blast.at ? ` · ${new Date(p.last_blast.at).toLocaleDateString()}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {p.status === 'published' && (
                      <Button asChild variant="outline" size="sm" data-testid={`admin-blog-view-${p.slug}`}>
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer">
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View
                          <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                        </a>
                      </Button>
                    )}
                    {p.status === 'published' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendBlast(p)}
                        className="border-[#004C54]/30 text-[#004C54] hover:bg-[#004C54]/10"
                        data-testid={`admin-blog-send-${p.slug}`}
                        title="Send (or re-send) this post to matching subscribers"
                      >
                        <Send className="h-3.5 w-3.5 mr-1" />
                        Send to subscribers
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleTogglePublish(p)} data-testid={`admin-blog-toggle-${p.slug}`}>
                      {p.status === 'published' ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`admin-blog-edit-${p.slug}`}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`admin-blog-delete-${p.slug}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {editor && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) closeEditor(); }}
          data-testid="admin-blog-editor"
        >
          <div className="w-full max-w-3xl bg-background rounded-2xl shadow-2xl my-4">
            <div className="sticky top-0 bg-background rounded-t-2xl border-b border-border/60 px-5 sm:px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="font-heading text-lg font-semibold">{editor.id ? 'Edit post' : 'New post'}</h3>
                <p className="text-[12px] text-foreground/55">{editor.id ? `Editing ${editor.slug || 'post'}` : 'Paste HTML, set metadata, publish.'}</p>
              </div>
              <button onClick={closeEditor} className="p-2 rounded-lg hover:bg-secondary/60" data-testid="admin-blog-editor-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 sm:px-6 py-5 space-y-5">
              {/* Title + Slug */}
              <div className="grid sm:grid-cols-[1fr_280px] gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="blog-title">Title *</Label>
                  <Input
                    id="blog-title"
                    value={editor.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="5 Steps to Manage Performance Anxiety…"
                    data-testid="admin-blog-field-title"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="blog-slug">Slug</Label>
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-foreground/55">
                      <input
                        type="checkbox"
                        checked={autoSlug}
                        onChange={(e) => setAutoSlug(e.target.checked)}
                        className="h-3 w-3"
                      />
                      auto
                    </label>
                  </div>
                  <Input
                    id="blog-slug"
                    value={editor.slug}
                    onChange={(e) => { setAutoSlug(false); updateField('slug', e.target.value); }}
                    placeholder="5-steps-performance-anxiety"
                    data-testid="admin-blog-field-slug"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="blog-subtitle">Subtitle / dek</Label>
                <Input
                  id="blog-subtitle"
                  value={editor.subtitle}
                  onChange={(e) => updateField('subtitle', e.target.value)}
                  placeholder="Short, punchy supporting line shown under the title."
                  data-testid="admin-blog-field-subtitle"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="blog-html">HTML body *</Label>
                <Textarea
                  id="blog-html"
                  value={editor.html}
                  onChange={(e) => updateField('html', e.target.value)}
                  rows={14}
                  className="font-mono text-[12.5px] leading-relaxed"
                  placeholder="<h1>Headline</h1>\n<p>Body...</p>"
                  data-testid="admin-blog-field-html"
                />
                <p className="text-[11.5px] text-foreground/55">Paste raw HTML. Headings, paragraphs, lists, images, and links all render. Styles are applied by our prose theme.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="blog-hero">Hero image URL</Label>
                  <Input
                    id="blog-hero"
                    value={editor.hero_image_url}
                    onChange={(e) => updateField('hero_image_url', e.target.value)}
                    placeholder="/blog/heroes/your-post.jpg or https://..."
                    data-testid="admin-blog-field-hero"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="blog-tags">Tags (comma-separated)</Label>
                  <Input
                    id="blog-tags"
                    value={editor.tags}
                    onChange={(e) => updateField('tags', e.target.value)}
                    placeholder="anxiety, confidence, apex-six"
                    data-testid="admin-blog-field-tags"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <Label htmlFor="blog-excerpt">Excerpt (shown on /blog cards)</Label>
                <Textarea
                  id="blog-excerpt"
                  value={editor.excerpt}
                  onChange={(e) => updateField('excerpt', e.target.value)}
                  rows={2}
                  placeholder="1–2 sentence pitch that appears in the index grid and RSS feed."
                  data-testid="admin-blog-field-excerpt"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="blog-meta-title">SEO title (optional)</Label>
                  <Input
                    id="blog-meta-title"
                    value={editor.meta_title}
                    onChange={(e) => updateField('meta_title', e.target.value)}
                    placeholder="Defaults to post title"
                    data-testid="admin-blog-field-meta-title"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="blog-canonical">Canonical URL (optional)</Label>
                  <Input
                    id="blog-canonical"
                    value={editor.canonical_url}
                    onChange={(e) => updateField('canonical_url', e.target.value)}
                    placeholder="Defaults to apexathleticmind.com/blog/{slug}"
                    data-testid="admin-blog-field-canonical"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="blog-meta-desc">SEO description (optional)</Label>
                <Textarea
                  id="blog-meta-desc"
                  value={editor.meta_description}
                  onChange={(e) => updateField('meta_description', e.target.value)}
                  rows={2}
                  placeholder="Defaults to excerpt. 150–160 chars optimal."
                  data-testid="admin-blog-field-meta-desc"
                />
              </div>

              <Separator />

              {/* Phase 27 — auto-blast to subscribers on publish. The backend
                  only honors this when the status actually transitions to
                  "published" in this save (either an initial create or a
                  draft→published edit). Defaults to ON for new drafts and
                  OFF when editing an already-published post (see openEdit). */}
              <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-secondary/30 p-3">
                <Switch
                  id="blog-send-blast"
                  checked={!!editor.send_to_subscribers_on_publish}
                  onCheckedChange={(checked) => updateField('send_to_subscribers_on_publish', checked)}
                  data-testid="admin-blog-field-send-blast"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label htmlFor="blog-send-blast" className="cursor-pointer inline-flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-[#004C54]" />
                    Email subscribers when published
                  </Label>
                  <p className="text-[11.5px] text-foreground/60 mt-1 leading-snug">
                    {editor.send_to_subscribers_on_publish
                      ? 'On — when this post goes live, a teaser will be sent to matching subscribers (smart-routed by tags). No effect if the post stays a draft.'
                      : 'Off — publish silently. You can still send manually later from the post row.'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    id="blog-status-toggle"
                    checked={editor.status === 'published'}
                    onCheckedChange={(checked) => updateField('status', checked ? 'published' : 'draft')}
                    data-testid="admin-blog-field-status"
                  />
                  <Label htmlFor="blog-status-toggle" className="cursor-pointer">
                    {editor.status === 'published' ? 'Published' : 'Draft'}
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={closeEditor} disabled={saving} data-testid="admin-blog-cancel">
                    Cancel
                  </Button>
                  <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} data-testid="admin-blog-save-draft">
                    <Save className="h-4 w-4 mr-1.5" />
                    Save
                  </Button>
                  <Button
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    className="bg-[#004C54] hover:bg-[#003a40] text-white"
                    data-testid="admin-blog-publish"
                  >
                    {saving ? 'Saving…' : editor.status === 'published' ? 'Update' : 'Publish'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {fbImport && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) closeFbImport(); }}
          data-testid="admin-blog-fb-import"
        >
          <div className="w-full max-w-2xl bg-background rounded-2xl shadow-2xl my-4">
            <div className="sticky top-0 bg-background rounded-t-2xl border-b border-border/60 px-5 sm:px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-[#1877F2]/10 text-[#1877F2] flex items-center justify-center shrink-0">
                  <FacebookGlyph className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold">Import from Facebook</h3>
                  <p className="text-[12px] text-foreground/55">
                    Paste a Facebook post. We&rsquo;ll create an editable draft &mdash; nothing publishes until you click Publish.
                  </p>
                </div>
              </div>
              <button
                onClick={closeFbImport}
                disabled={fbImporting}
                className="p-2 rounded-lg hover:bg-secondary/60 disabled:opacity-40"
                data-testid="admin-blog-fb-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-5 sm:px-6 py-5 space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="fb-url">Facebook post URL</Label>
                <Input
                  id="fb-url"
                  value={fbImport.facebook_url}
                  onChange={(e) => updateFbField('facebook_url', e.target.value)}
                  placeholder="https://www.facebook.com/your-page/posts/..."
                  data-testid="admin-blog-fb-url"
                />
                <p className="text-[11.5px] text-foreground/55">
                  Optional. Stored with the draft for provenance &mdash; never displayed publicly.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="fb-text">Facebook post text *</Label>
                <Textarea
                  id="fb-text"
                  value={fbImport.text}
                  onChange={(e) => updateFbField('text', e.target.value)}
                  rows={10}
                  className="leading-relaxed"
                  placeholder="Paste the full text of the post here..."
                  data-testid="admin-blog-fb-text"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="fb-image">Image URL</Label>
                <Input
                  id="fb-image"
                  value={fbImport.image_url}
                  onChange={(e) => updateFbField('image_url', e.target.value)}
                  placeholder="https://... (optional — used as hero image)"
                  data-testid="admin-blog-fb-image"
                />
              </div>

              <Separator />

              <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-secondary/30 p-3">
                <Switch
                  id="fb-use-ai"
                  checked={!!fbImport.use_ai}
                  onCheckedChange={(checked) => updateFbField('use_ai', checked)}
                  data-testid="admin-blog-fb-use-ai"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label htmlFor="fb-use-ai" className="cursor-pointer inline-flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-[#004C54]" />
                    Use AI formatting
                  </Label>
                  <p className="text-[11.5px] text-foreground/60 mt-1 leading-snug">
                    {fbImport.use_ai
                      ? 'AI will generate a blog title, clean paragraphs, subheadings, and a short CTA. Uses LLM credits.'
                      : 'Off — content saved exactly as pasted, wrapped in basic paragraphs. Zero LLM credit usage.'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={closeFbImport}
                  disabled={fbImporting}
                  data-testid="admin-blog-fb-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleFbCreateDraft}
                  disabled={fbImporting || !fbImport.text.trim()}
                  className="bg-[#004C54] hover:bg-[#003a40] text-white"
                  data-testid="admin-blog-fb-create-draft"
                >
                  {fbImporting ? (
                    <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Creating draft…</>
                  ) : (
                    <><FileText className="h-4 w-4 mr-1.5" /> Create Draft</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
